import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import {
  LuBot, LuPlus, LuMessageSquare, LuUsers, LuChartBar, LuSettings,
  LuCalendar, LuLogOut, LuEllipsisVertical, LuPencil, LuTrash2, LuPlay, LuPause,
  LuBookOpen, LuZap, LuWebhook, LuPhone, LuCode, LuCopy, LuCheck, LuShield, LuShieldAlert,
  LuFileText, LuMenu
} from "react-icons/lu";
import { FaWhatsapp, FaGoogle } from "react-icons/fa";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "../components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Sheet, SheetContent } from "../components/ui/sheet";
import { API } from "../App";

const DashboardPage = ({ auth }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [agents, setAgents] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [calendarConnections, setCalendarConnections] = useState([]);
  const [showEmbedDialog, setShowEmbedDialog] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [copied, setCopied] = useState(false);
  const [whatsappConfig, setWhatsappConfig] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    // Check for calendar connection callback
    if (searchParams.get("calendar_connected") === "true") {
      toast.success("¡Google Calendar conectado exitosamente!");
      // Update user state
      auth.setUser({ ...auth.user, google_calendar_connected: true });
    }
    if (searchParams.get("calendar_error")) {
      const errorMsg = searchParams.get("calendar_error");
      toast.error(`Error de Google Calendar: ${errorMsg}`);
    }

    fetchData();
  }, [searchParams]);

  const fetchData = async () => {
    try {
      const [agentsRes, analyticsRes] = await Promise.all([
        fetch(`${API}/agents`, {
          headers: { Authorization: `Bearer ${auth.token}` }
        }),
        fetch(`${API}/analytics`, {
          headers: { Authorization: `Bearer ${auth.token}` }
        })
      ]);

      if (agentsRes.ok) {
        const agentsData = await agentsRes.json();
        setAgents(agentsData);
      }
      if (analyticsRes.ok) {
        const analyticsData = await analyticsRes.json();
        setAnalytics(analyticsData);
      }

      const connectionsRes = await fetch(`${API}/calendar/connections`, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      if (connectionsRes.ok) {
        const connData = await connectionsRes.json();
        setCalendarConnections(connData);
      }

      const waRes = await fetch(`${API}/whatsapp/config`, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      if (waRes.ok) {
        const waData = await waRes.json();
        setWhatsappConfig(waData);
      }
    } catch (error) {
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAgent = async (agentId) => {
    if (!window.confirm("¿Estás seguro de eliminar este agente?")) return;

    try {
      const response = await fetch(`${API}/agents/${agentId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${auth.token}` }
      });

      if (response.ok) {
        toast.success("Agente eliminado");
        setAgents(agents.filter(a => a.id !== agentId));
      } else {
        toast.error("Error al eliminar agente");
      }
    } catch (error) {
      toast.error("Error de conexión");
    }
  };

  const handleToggleAgent = async (agent) => {
    try {
      const response = await fetch(`${API}/agents/${agent.id}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${auth.token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ is_active: !agent.is_active })
      });

      if (response.ok) {
        toast.success(agent.is_active ? "Agente pausado" : "Agente activado");
        setAgents(agents.map(a =>
          a.id === agent.id ? { ...a, is_active: !a.is_active } : a
        ));
      }
    } catch (error) {
      toast.error("Error al actualizar agente");
    }
  };

  const connectGoogleCalendar = async () => {
    try {
      const response = await fetch(`${API}/oauth/calendar/login`, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      const data = await response.json();
      if (data.authorization_url) {
        window.location.href = data.authorization_url;
      }
    } catch (error) {
      toast.error("Error al conectar con Google Calendar");
    }
  };

  const deleteCalendarConnection = async (connId) => {
    if (!window.confirm("¿Desvincular esta cuenta de Google?")) return;
    try {
      const response = await fetch(`${API}/calendar/connections/${connId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      if (response.ok) {
        toast.success("Cuenta desvinculada");
        setCalendarConnections(calendarConnections.filter(c => c.id !== connId));
        fetchData(); // Actualizar el estado global de conexión
      }
    } catch (error) {
      toast.error("Error al desvincular");
    }
  };

  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    toast.success("Código copiado al portapapeles");
    setTimeout(() => setCopied(false), 2000);
  };

  const getEmbedCode = (agent) => {
    // In production, this should use the real API URL
    const apiUrl = API.startsWith('http') ? API.split('/api')[0] : window.location.origin;

    return `<!-- BotNexus Widget - ${agent.name} -->
<script 
  src="${apiUrl}/static/widget.js" 
  data-agent-id="${agent.id}" 
  data-api-url="${apiUrl}"
  data-name="${agent.name}"
  data-color="#4F46E5"
  data-widget-type="${agent.widget_type || 'chat'}"
  data-phone="${agent.whatsapp_phone || ''}"
></script>`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  const sidebarContent = (
    <>
      <Link to="/dashboard" className="flex items-center gap-3 mb-10" onClick={() => setSidebarOpen(false)}>
        <div className="w-10 h-10 rounded-xl gradient-hero flex items-center justify-center">
          <LuBot className="w-6 h-6 text-white" />
        </div>
        <span className="font-heading font-bold text-xl text-foreground">BotNexus</span>
      </Link>

      <nav className="flex-1 space-y-2 overflow-y-auto">
          <Link to="/dashboard" onClick={() => setSidebarOpen(false)} className="flex items-center gap-3 px-4 py-3 rounded-xl bg-primary/10 text-primary font-medium">
            <LuChartBar className="w-5 h-5" />
            Dashboard
          </Link>
          <Link to="/calendar" onClick={() => setSidebarOpen(false)} className="flex items-center gap-3 px-4 py-3 rounded-xl text-muted-foreground hover:bg-muted/50 transition-colors">
            <LuCalendar className="w-5 h-5" />
            Calendario
          </Link>
          <Link to="/whatsapp-setup" onClick={() => setSidebarOpen(false)} className="flex items-center gap-3 px-4 py-3 rounded-xl text-muted-foreground hover:bg-muted/50 transition-colors">
            <LuWebhook className="w-5 h-5" />
            WhatsApp Config
          </Link>
          <Link to="/templates" onClick={() => setSidebarOpen(false)} className="flex items-center gap-3 px-4 py-3 rounded-xl text-muted-foreground hover:bg-muted/50 transition-colors">
            <LuFileText className="w-5 h-5" />
            Plantillas
          </Link>
          <Link to="/phone-assignments" onClick={() => setSidebarOpen(false)} className="flex items-center gap-3 px-4 py-3 rounded-xl text-muted-foreground hover:bg-muted/50 transition-colors">
            <LuPhone className="w-5 h-5" />
            Asignaciones
          </Link>
          <Link to="/leads" onClick={() => setSidebarOpen(false)} className="flex items-center gap-3 px-4 py-3 rounded-xl text-muted-foreground hover:bg-muted/50 transition-colors">
            <LuUsers className="w-5 h-5" />
            Leads / Opt-ins
          </Link>

          {auth.user?.role === "admin" && (
            <div className="space-y-1 mt-4">
              <Link to="/admin/users" onClick={() => setSidebarOpen(false)} className="flex items-center gap-3 px-4 py-3 rounded-xl text-primary font-bold bg-primary/5 hover:bg-primary/10 transition-colors border border-primary/20">
                <LuShield className="w-5 h-5 text-primary" />
                Administrar Usuarios
              </Link>
              <Link to="/admin/logs" onClick={() => setSidebarOpen(false)} className="flex items-center gap-3 px-4 py-3 rounded-xl text-destructive font-bold bg-destructive/5 hover:bg-destructive/10 transition-colors border border-destructive/20">
                <LuShieldAlert className="w-5 h-5 text-destructive" />
                Logs de Errores
              </Link>
            </div>
          )}

          <div className="pt-4 mt-4 border-t border-white/5">
            <p className="text-[10px] uppercase font-bold text-muted-foreground px-4 mb-2 tracking-wider">Cuentas de Google</p>
            <div className="space-y-1">
              {calendarConnections.map(conn => (
                <div key={conn.id} className="group flex items-center justify-between px-4 py-2 rounded-lg hover:bg-white/5 transition-colors">
                  <div className="flex items-center gap-2 min-w-0">
                    <FaGoogle className="w-3 h-3 text-primary shrink-0" />
                    <span className="text-xs text-muted-foreground truncate">{conn.email}</span>
                  </div>
                  <button
                    onClick={() => deleteCalendarConnection(conn.id)}
                    className="opacity-0 group-hover:opacity-100 p-1 hover:text-destructive transition-all"
                  >
                    <LuTrash2 className="w-3 h-3" />
                  </button>
                </div>
              ))}
              <button
                onClick={connectGoogleCalendar}
                className="w-full flex items-center gap-2 px-4 py-2 text-xs text-primary hover:bg-primary/5 rounded-lg transition-colors"
              >
                <LuPlus className="w-3 h-3" />
                Vincular cuenta
              </button>
            </div>
          </div>
        </nav>

        <div className="space-y-4 pt-6 border-t border-border">
          <div className="flex items-center gap-3 px-2">
            <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
              <span className="text-primary font-semibold">
                {auth.user?.name?.charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-foreground truncate">{auth.user?.name}</p>
              <p className="text-xs text-muted-foreground truncate">{auth.user?.email}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            className="w-full justify-start text-muted-foreground hover:text-foreground"
            onClick={() => {
              auth.logout();
              navigate("/");
            }}
            data-testid="logout-btn"
          >
            <LuLogOut className="w-5 h-5 mr-3" />
            Cerrar Sesión
          </Button>
        </div>
    </>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Sidebar fija (desktop) */}
      <aside className="hidden md:flex fixed left-0 top-0 bottom-0 w-64 glass border-r border-white/10 p-6 flex-col">
        {sidebarContent}
      </aside>

      {/* Sidebar como drawer (móvil) */}
      <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
        <SheetContent side="left" className="w-72 max-w-[85vw] p-6 flex flex-col glass border-white/10">
          {sidebarContent}
        </SheetContent>
      </Sheet>

      {/* Header móvil */}
      <header className="md:hidden sticky top-0 z-40 glass border-b border-white/10 px-4 py-3 flex items-center justify-between">
        <Link to="/dashboard" className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg gradient-hero flex items-center justify-center">
            <LuBot className="w-5 h-5 text-white" />
          </div>
          <span className="font-heading font-bold text-lg text-foreground">BotNexus</span>
        </Link>
        <Button
          variant="ghost"
          size="icon"
          className="rounded-xl"
          onClick={() => setSidebarOpen(true)}
          aria-label="Abrir menú"
        >
          <LuMenu className="w-6 h-6" />
        </Button>
      </header>

      {/* Main Content */}
      <main className="md:ml-64 p-4 sm:p-6 md:p-8">
        {/* Banner token WhatsApp caducado */}
        {whatsappConfig?.token_status === "expired" && (
          <div className="flex flex-col sm:flex-row items-start gap-4 p-4 mb-6 rounded-2xl bg-red-500/10 border border-red-500/30">
            <LuShieldAlert className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-red-400 text-sm">Token de WhatsApp caducado — los mensajes no se están enviando</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Genera un nuevo access token en Meta for Developers y actualiza tus credenciales para restaurar el servicio.
                {whatsappConfig.token_status_updated_at && (
                  <> Detectado el {new Date(whatsappConfig.token_status_updated_at).toLocaleString("es-ES")}.</>
                )}
              </p>
            </div>
            <Link to="/whatsapp-setup">
              <Button size="sm" className="rounded-full bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/30 shrink-0">
                Renovar token
              </Button>
            </Link>
          </div>
        )}

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-foreground">
              Hola, {auth.user?.name?.split(" ")[0]}
            </h1>
            <p className="text-muted-foreground mt-1">Gestiona tus agentes de WhatsApp</p>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
            <Button
              variant="outline"
              className="rounded-full border-white/20"
              onClick={connectGoogleCalendar}
              data-testid="connect-calendar-btn"
            >
              <FaGoogle className="w-4 h-4 mr-2" />
              Vincular Calendar
            </Button>
            <Link to="/agents/new">
              <Button className="w-full rounded-full bg-primary hover:bg-primary/90 glow-primary" data-testid="create-agent-btn">
                <LuPlus className="w-5 h-5 mr-2" />
                Nuevo Agente
              </Button>
            </Link>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card className="glass-card border-white/10" data-testid="stat-total-agents">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-muted-foreground text-sm">Total Agentes</p>
                    <p className="font-heading text-3xl font-bold text-foreground mt-1">
                      {analytics?.total_agents || 0}
                    </p>
                  </div>
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                    <LuBot className="w-6 h-6 text-primary" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card className="glass-card border-white/10" data-testid="stat-active-agents">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-muted-foreground text-sm">Agentes Activos</p>
                    <p className="font-heading text-3xl font-bold text-foreground mt-1">
                      {analytics?.active_agents || 0}
                    </p>
                  </div>
                  <div className="w-12 h-12 rounded-xl bg-secondary/20 flex items-center justify-center">
                    <LuZap className="w-6 h-6 text-secondary" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Card className="glass-card border-white/10" data-testid="stat-conversations">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-muted-foreground text-sm">Conversaciones</p>
                    <p className="font-heading text-3xl font-bold text-foreground mt-1">
                      {analytics?.total_conversations || 0}
                    </p>
                  </div>
                  <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center">
                    <LuMessageSquare className="w-6 h-6 text-accent" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <Card className="glass-card border-white/10" data-testid="stat-messages-today">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-muted-foreground text-sm">Mensajes Hoy</p>
                    <p className="font-heading text-3xl font-bold text-foreground mt-1">
                      {analytics?.messages_today || 0}
                    </p>
                  </div>
                  <div className="w-12 h-12 rounded-xl bg-chart-4/10 flex items-center justify-center">
                    <FaWhatsapp className="w-6 h-6 text-chart-4" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Agents List */}
        <div className="mb-8">
          <h2 className="font-heading text-xl font-semibold text-foreground mb-4">Tus Agentes</h2>

          {agents.length === 0 ? (
            <Card className="glass-card border-white/10 border-dashed">
              <CardContent className="p-12 text-center">
                <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <LuBot className="w-8 h-8 text-primary" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-foreground mb-2">
                  Crea tu primer agente
                </h3>
                <p className="text-muted-foreground mb-6 max-w-sm mx-auto">
                  Los agentes de WhatsApp responden automáticamente a tus clientes usando IA
                </p>
                <Link to="/agents/new">
                  <Button className="rounded-full bg-primary hover:bg-primary/90" data-testid="empty-create-agent-btn">
                    <LuPlus className="w-5 h-5 mr-2" />
                    Crear Agente
                  </Button>
                </Link>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {agents.map((agent, index) => (
                <motion.div
                  key={agent.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="glass-card border-white/10 hover:border-primary/30 transition-all duration-300 group" data-testid={`agent-card-${agent.id}`}>
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                            <LuBot className="w-5 h-5 text-primary" />
                          </div>
                          <div className="flex flex-col">
                            <div className="flex items-center gap-2">
                              <CardTitle className="text-lg text-foreground">{agent.name}</CardTitle>
                              {auth.user?.role === "admin" && (
                                <div className="flex flex-wrap gap-1">
                                  {agent.owner_email && (
                                    <Badge variant="outline" className="text-[10px] py-0 px-1 font-normal opacity-70 bg-primary/5">
                                      {agent.owner_email}
                                    </Badge>
                                  )}
                                  {agent.collaborator_emails?.length > 0 && (
                                    <Badge variant="outline" className="text-[10px] py-0 px-1 font-normal opacity-70 bg-secondary/5">
                                      +{agent.collaborator_emails.length} colab.
                                    </Badge>
                                  )}
                                </div>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground">{agent.ai_model}</p>
                          </div>
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`agent-menu-${agent.id}`}>
                              <LuEllipsisVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="bg-card border-border">
                            <DropdownMenuItem onClick={() => navigate(`/agents/${agent.id}`)}>
                              <LuPencil className="w-4 h-4 mr-2" />
                              Editar
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleToggleAgent(agent)}>
                              {agent.is_active ? (
                                <>
                                  <LuPause className="w-4 h-4 mr-2" />
                                  Pausar
                                </>
                              ) : (
                                <>
                                  <LuPlay className="w-4 h-4 mr-2" />
                                  Activar
                                </>
                              )}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => {
                              setSelectedAgent(agent);
                              setShowEmbedDialog(true);
                            }}>
                              <LuCode className="w-4 h-4 mr-2" />
                              Código de Inserción
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="text-destructive"
                              onClick={() => handleDeleteAgent(agent.id)}
                            >
                              <LuTrash2 className="w-4 h-4 mr-2" />
                              Eliminar
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground line-clamp-2 mb-4">
                        {agent.description || "Sin descripción"}
                      </p>
                      <div className="flex items-center gap-2 mb-4">
                        <Badge variant={agent.is_active ? "default" : "secondary"} className={agent.is_active ? "bg-green-500/20 text-green-400 border-green-500/30" : ""}>
                          {agent.is_active ? "Activo" : "Pausado"}
                        </Badge>
                        {agent.calendar_enabled && (
                          <div className="flex flex-col gap-1">
                            <Badge variant="outline" className="border-primary/30 text-primary w-fit">
                              <LuCalendar className="w-3 h-3 mr-1" />
                              {agent.calendar_name || "Calendar"}
                            </Badge>
                            {agent.calendar_account && (
                              <span className="text-[10px] text-muted-foreground ml-1 truncate max-w-[150px]">
                                {agent.calendar_account}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center justify-between text-sm text-muted-foreground mb-4">
                        <span className="flex items-center gap-1">
                          <LuMessageSquare className="w-4 h-4" />
                          {agent.messages_count} mensajes
                        </span>
                      </div>
                      <div className="flex gap-2">
                        <Link to={`/agents/${agent.id}/chat`} className="flex-1">
                          <Button variant="outline" className="w-full rounded-xl border-white/20" data-testid={`agent-test-${agent.id}`}>
                            <LuMessageSquare className="w-4 h-4 mr-2" />
                            Probar
                          </Button>
                        </Link>
                        <Link to={`/agents/${agent.id}/knowledge`}>
                          <Button variant="ghost" size="icon" className="rounded-xl" data-testid={`agent-knowledge-${agent.id}`}>
                            <LuBookOpen className="w-4 h-4" />
                          </Button>
                        </Link>
                        <Link to={`/agents/${agent.id}/conversations`}>
                          <Button variant="ghost" size="icon" className="rounded-xl" data-testid={`agent-conversations-${agent.id}`}>
                            <LuUsers className="w-4 h-4" />
                          </Button>
                        </Link>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </div>

        {/* Embed Code Dialog */}
        <Dialog open={showEmbedDialog} onOpenChange={setShowEmbedDialog}>
          <DialogContent className="sm:max-w-2xl bg-card border-border">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <LuCode className="w-5 h-5 text-primary" />
                Inyectar {selectedAgent?.name} en tu web
              </DialogTitle>
              <DialogDescription>
                Copia y pega este código antes de cerrar la etiqueta <code>&lt;/body&gt;</code> de tu sitio web.
              </DialogDescription>
            </DialogHeader>
            <div className="mt-4 space-y-4">
              <div className="relative">
                <pre className="p-4 bg-muted rounded-xl overflow-x-auto text-xs font-mono text-foreground border border-white/10">
                  {selectedAgent && getEmbedCode(selectedAgent)}
                </pre>
                <Button
                  size="sm"
                  variant="secondary"
                  className="absolute top-2 right-2 h-8 rounded-lg"
                  onClick={() => handleCopyCode(getEmbedCode(selectedAgent))}
                >
                  {copied ? <LuCheck className="w-4 h-4" /> : <LuCopy className="w-4 h-4" />}
                </Button>
              </div>
              <div className="p-4 rounded-xl bg-primary/5 border border-primary/10">
                <h4 className="text-sm font-semibold text-primary mb-2 flex items-center gap-2">
                  <FaWhatsapp className="w-4 h-4" />
                  Opción B: Botón directo a WhatsApp
                </h4>
                <p className="text-xs text-muted-foreground mb-3">
                  Si prefieres que los usuarios abran directamente WhatsApp en sus teléfonos:
                </p>
                <code className="block p-3 bg-muted rounded-lg text-xs break-all">
                  {`<a href="https://wa.me/${selectedAgent?.whatsapp_phone?.replace(/[^0-9]/g, '') || 'TU_NUMERO'}" target="_blank">Chatea con nosotros</a>`}
                </code>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default DashboardPage;
